import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "styles/About.module.scss";
import { useTranslation } from "react-i18next";
import Spacer from "components/Spacer";
import clsx from "clsx";
import Image from "next/image";
import { Waypoint } from "react-waypoint";
import { Fade } from "components/Fade";
import "swiper/css";
import SwiperCore from "swiper";
import { useWindowSize } from "../utils/hooks";
import { tabletMaxWidth } from "../utils/breakpoints";
import { Swiper, SwiperSlide } from "swiper/react";
import { useRouter } from "next/router";
import { NextPage } from "next";
import screenshot3 from "../public/about/screenshot-3.png";
import screenshot4 from "../public/about/screenshot-4.png";
import inspiredScreenshot1 from "../public/about/inspiredScreenshot-1.png";
import inspiredScreenshot2 from "../public/about/inspiredScreenshot-2.png";
import inspiredScreenshot3 from "../public/about/inspiredScreenshot-3.png";
import inspiredScreenshot4 from "../public/about/inspiredScreenshot-4.png";
import inspiredScreenshot5 from "../public/about/inspiredScreenshot-5.png";
import inspiredScreenshot6 from "../public/about/inspiredScreenshot-6.png";
import { Header, HeaderVariant } from "../components/Header";
import Link from "next/link";
import nftInstructions from "../public/faqs/nft-instructions-4.png";
import { ImageToggle } from "../components/ImageToggle";
import { Meta } from "../components/Meta";
import { TwitterTweetEmbed } from "react-twitter-embed";
import { DelayRender } from "../components/DelayRender";
import { MediaQueryContext } from "../context/mediaQuery/context";

const About: React.FC<NextPage> = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const [stickyContainerInfo, setStickyContainerInfo] = useState<{
    left: number;
    top: number;
    height: number;
  }>({ left: 0, top: 0, height: 0 });
  const [isVideoVisible, setIsVideoVisible] = useState(false);
  const {
    state: { tablet },
  } = useContext(MediaQueryContext);
  const calculateStickyContainer = useCallback(() => {
    const startStickyEl = document.getElementById("startSticky");
    const stopStickyEl = document.getElementById("stopSticky");
    if (startStickyEl && stopStickyEl) {
      const height = stopStickyEl.offsetTop - startStickyEl.offsetTop;
      setStickyContainerInfo({
        height,
        left: startStickyEl.offsetLeft,
        top: startStickyEl.offsetTop,
      });
    }
  }, []);

  const windowSize = useWindowSize();
  useEffect(() => {
    if (tablet) return;

    calculateStickyContainer();
  }, [tablet, windowSize.width, calculateStickyContainer]);

  const imageSize = useMemo(() => ({ width: 289, height: 386 }), []);
  const nftInstructionsImage = useMemo(
    () => (
      <Image
        {...imageSize}
        placeholder={"blur"}
        src={nftInstructions}
        alt={"xNFT"}
        layout={"fixed"}
      />
    ),
    [imageSize]
  );

  return (
    <>
      <Meta title={t("about.text")}>
        <link rel="preconnect" href="https://platform.twitter.com" />
        <link rel="preconnect" href="https://rcsa.s3.eu-west-2.amazonaws.com" />
      </Meta>
      <div className={clsx(styles.container, "column ai-center")}>
        <div
          style={{
            position: "absolute",
            top: stickyContainerInfo.top,
            left: stickyContainerInfo.left,
            height: stickyContainerInfo.height,
            zIndex: 0,
            opacity: stickyContainerInfo.height === 0 ? 0 : 1,
          }}
          className={"hideOnTablet"}
        >
          <div className={styles.stickyCard}>{nftInstructionsImage}</div>
        </div>
        <Header variant={HeaderVariant.dark} />
        <div className={clsx(styles.section1, "column ai-center")}>
          <Spacer height={40} />
          <div className={clsx("column ai-center", styles.ideaSection)}>
            <div
              className={clsx(
                styles.headline,
                styles.ideaHeadline,
                "text-align-center"
              )}
            >
              {t("about.idea.headline")}
            </div>
            <Spacer height={4} className={"hideOnTablet"} />
            <Spacer height={6} className={"hideOnDesktop"} />
            <p
              className={clsx(
                styles.p,
                styles.light,
                styles.ideaText,
                "text-align-center"
              )}
              dangerouslySetInnerHTML={{
                __html: t("about.idea.text"),
              }}
            />
            <Spacer height={34} />
            <ImageToggle />
          </div>
          <Spacer height={44} />
        </div>

        <div className={clsx("column ai-center", styles.section2)}>
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
          <div
            className={clsx(styles.headline, {
              "text-align-center": !tablet,
              "text-align-left": tablet,
            })}
          >
            {t("about.why.headline")}
          </div>
          <Spacer height={20} className={"hideOnTablet"} />
          <Spacer height={6} className={"hideOnDesktop"} />
          <div
            dangerouslySetInnerHTML={{ __html: t("about.why.text") }}
            className={clsx(styles.p, styles.whyText)}
          />
          <Spacer height={144} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
        </div>

        <div
          className={clsx(
            "column ai-center",
            styles.section,
            styles.inspiredBySection
          )}
        >
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
          <div className={clsx(styles.headline, styles.inspiredBy)}>
            {t("about.inspiredBy.text")}
          </div>
          <Spacer height={40} className={"hideOnTablet"} />
          <Spacer height={16} className={"hideOnDesktop"} />
          <div className={styles.separator} />
          <Spacer height={48} className={"hideOnTablet"} />
          <Spacer height={32} className={"hideOnDesktop"} />
          <InspiredByCards tablet={tablet} />
          <Spacer height={48} className={"hideOnTablet"} />
          <Spacer height={23} className={"hideOnDesktop"} />
          <div className={styles.separator} />
          <div>
            <Spacer height={150} className={"hideOnTablet"} />
            <Spacer height={100} className={"hideOnDesktop"} />
            <div className={clsx(styles.headline, "text-align-center")}>
              {t("about.goals.headline")}
            </div>
            <Spacer height={40} />
            <div className={"rowDesktopColumnTablet"}>
              <div className={styles.goalsCard}>
                <p
                  dangerouslySetInnerHTML={{ __html: t("about.goals.p1") }}
                  className={clsx(styles.p, "text-align-center")}
                />
              </div>
              <Spacer width={15} className={"hideOnTablet"} />
              <Spacer height={23} className={"hideOnDesktop"} />
              <div className={styles.goalsCard}>
                <p
                  dangerouslySetInnerHTML={{ __html: t("about.goals.p2") }}
                  className={clsx(styles.p, "text-align-center")}
                />
              </div>
              <Spacer width={15} className={"hideOnTablet"} />
              <Spacer height={23} className={"hideOnDesktop"} />
              <div className={styles.goalsCard}>
                <p
                  dangerouslySetInnerHTML={{ __html: t("about.goals.p3") }}
                  className={clsx(styles.p, "text-align-center")}
                />
              </div>
            </div>
            <Spacer height={150} className={"hideOnTablet"} />
            <Spacer height={100} className={"hideOnDesktop"} />
          </div>
        </div>

        <div
          className={clsx(
            styles.section2,
            styles.tokenisedIdea,
            "column ai-center"
          )}
        >
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
          <div
            className={clsx("column", {
              "ai-start": tablet,
              "ai-center": !tablet,
            })}
          >
            <div className={styles.headline}>
              {t("about.tokenisedIdea.headline")}
            </div>
            <Spacer height={6} className={"hideOnTablet"} />
            <p className={clsx(styles.p, styles.subtitle)}>
              {t("about.tokenisedIdea.text")}
            </p>
          </div>
          <Spacer height={58} className={"hideOnTablet"} />
          <Spacer height={40} className={"hideOnDesktop"} />
          <div className={"rowDesktopColumnTablet"}>
            {!tablet && <Waypoint onEnter={calculateStickyContainer} />}
            <div id={"startSticky"} />
            {tablet ? (
              <div className={"as-center"}>{nftInstructionsImage}</div>
            ) : (
              <Spacer {...imageSize} />
            )}
            <Spacer width={64} className={"hideOnTablet"} />
            <div className={styles.paragraphContainer}>
              <Spacer height={40} className={"hideOnTablet"} />
              <Spacer height={22} className={"hideOnDesktop"} />
              <Fade disabled={tablet}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t("about.tokenisedIdea.p1"),
                  }}
                  className={styles.p}
                />
              </Fade>
              <Spacer height={imageSize.height} className={"hideOnTablet"} />
              <Fade disabled={tablet}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t("about.tokenisedIdea.p2"),
                  }}
                  className={styles.p}
                />
              </Fade>
              <Spacer height={imageSize.height} className={"hideOnTablet"} />
              <Fade disabled={tablet}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t("about.tokenisedIdea.p3"),
                  }}
                  className={styles.p}
                />
              </Fade>
              <Spacer height={imageSize.height} className={"hideOnTablet"} />
              <Fade disabled={tablet}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t("about.tokenisedIdea.p4"),
                  }}
                  className={styles.p}
                />
              </Fade>
              <Spacer height={imageSize.height} className={"hideOnTablet"} />
              <Fade disabled={tablet}>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t("about.tokenisedIdea.p5"),
                  }}
                  className={styles.p}
                />
              </Fade>
            </div>
          </div>
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={82} className={"hideOnDesktop"} />
        </div>

        <div
          className={clsx(
            "column ai-center",
            styles.section,
            styles.aboveSticky,
            styles.tweets
          )}
        >
          <Spacer height={107} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
          <div className={clsx(styles.headline, "text-align-center")}>
            {t("about.meme.headline")}
          </div>
          <Spacer height={6} className={"hideOnTablet"} />
          <Spacer height={24} className={"hideOnDesktop"} />
          <p
            dangerouslySetInnerHTML={{
              __html: t("about.meme.text"),
            }}
            className={clsx(styles.p, styles.memeText)}
          />
          <Spacer height={62} className={"hideOnTablet"} />
          <Spacer height={22} className={"hideOnDesktop"} />
          {!tablet && <Waypoint onEnter={calculateStickyContainer} />}
          <Tweets tablet={tablet} />
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={120} className={"hideOnDesktop"} />
        </div>

        <div className={clsx(styles.section1, styles.aboveSticky)}>
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
          <div className={"rowDesktopColumnTabletReverse ai-center jc-center"}>
            <p
              dangerouslySetInnerHTML={{
                __html: t("about.artists"),
              }}
              className={clsx(styles.p, styles.light, styles.artistText)}
            />
            <Spacer width={80} className={"hideOnTablet"} />
            <Spacer height={15} className={"hideOnDesktop"} />
            <div className={"row"}>
              <Image
                placeholder={"blur"}
                src={screenshot4}
                width={tablet ? 154 : imageSize.width}
                height={tablet ? 229 : imageSize.height}
                alt={"screenshot 1"}
                layout={"fixed"}
              />
              <Spacer width={40} className={"hideOnTablet"} />
              <Spacer width={29} className={"hideOnDesktop"} />
              <Image
                placeholder={"blur"}
                src={screenshot3}
                width={tablet ? 154 : imageSize.width}
                height={tablet ? 229 : imageSize.height}
                alt={"screenshot 2"}
                layout={"fixed"}
              />
            </div>
          </div>
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={75} className={"hideOnDesktop"} />
        </div>

        <div className={clsx("column ai-center", styles.section)}>
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
          <div className={clsx(styles.headline, "text-align-center")}>
            {t("about.enter.headline")}
          </div>
          <Spacer height={80} className={"hideOnTablet"} />
          <Spacer height={40} className={"hideOnDesktop"} />
          <div className={"rowDesktopColumnTablet ai-center"}>
            {tablet ? nftInstructionsImage : <Spacer {...imageSize} />}
            <Spacer width={64} className={"hideOnTablet"} />
            <Spacer height={22} className={"hideOnDesktop"} />
            <div className={styles.paragraphContainer}>
              <p
                dangerouslySetInnerHTML={{ __html: t("about.enter.p1") }}
                className={styles.p}
              />
            </div>
          </div>
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={82} className={"hideOnDesktop"} />
        </div>

        <div
          style={{ overflow: "hidden" }}
          className={clsx("w-full", styles.aboveSticky)}
        >
          <div className={clsx(styles.section3, "w-full column")}>
            <div className={clsx(styles.section3BG, "hideOnDesktop")} />
            <div className={styles.section3Content}>
              <Spacer height={150} className={"hideOnTablet"} />
              <Spacer height={100} className={"hideOnDesktop"} />
              <div className={clsx(styles.headline, styles.withRCSA)}>
                {t("about.withRCSA.headline1")}
              </div>
              <Spacer height={64} />
              <div
                className={clsx("rowDesktopColumnTablet jc-center w-full", {
                  "ai-center": !tablet,
                })}
              >
                <div className={clsx("column", { "ai-center": !tablet })}>
                  <div className={styles.withRCSALine1}>
                    {t("about.withRCSA.p1.line1")}
                  </div>
                  <div className={styles.withRCSALine2}>
                    {t("about.withRCSA.p1.line2")}
                  </div>
                </div>
                <Spacer width={91} className={"hideOnTablet"} />
                <Spacer height={40} className={"hideOnDesktop"} />
                <div className={clsx("column", { "ai-center": !tablet })}>
                  <div className={styles.withRCSALine1}>
                    {t("about.withRCSA.p2.line1")}
                  </div>
                  <div className={styles.withRCSALine2}>
                    {t("about.withRCSA.p2.line2")}
                  </div>
                </div>
                <Spacer width={91} className={"hideOnTablet"} />
                <Spacer height={40} className={"hideOnDesktop"} />
                <div className={clsx("column", { "ai-center": !tablet })}>
                  <div className={styles.withRCSALine1}>
                    {t("about.withRCSA.p3.line1")}
                  </div>
                  <div className={styles.withRCSALine2}>
                    {t("about.withRCSA.p3.line2")}
                  </div>
                </div>
                <Spacer width={91} className={"hideOnTablet"} />
                <Spacer height={40} className={"hideOnDesktop"} />
                <div className={clsx("column", { "ai-center": !tablet })}>
                  <div className={styles.withRCSALine1}>
                    {t("about.withRCSA.p4.line1")}
                  </div>
                  <div className={styles.withRCSALine2}>
                    {t("about.withRCSA.p4.line2")}
                  </div>
                </div>
              </div>
              <Spacer height={64} />
              <div className={clsx(styles.headline, styles.withRCSA)}>
                {t("about.withRCSA.headline2")}
              </div>
              <Spacer height={150} className={"hideOnTablet"} />
              <Spacer height={100} className={"hideOnDesktop"} />
            </div>
          </div>
        </div>
        {!tablet && <Waypoint onEnter={calculateStickyContainer} />}
        <div className={styles.section}>
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
          <div className={"rowDesktopColumnTablet ai-center"}>
            {tablet ? nftInstructionsImage : <Spacer {...imageSize} />}
            <Spacer width={64} className={"hideOnTablet"} />
            <Spacer height={22} className={"hideOnDesktop"} />
            <div className={styles.paragraphContainer}>
              <p
                dangerouslySetInnerHTML={{ __html: t("about.enter.p2") }}
                className={styles.p}
              />
            </div>
          </div>
          <div id={"stopSticky"} />
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={82} className={"hideOnDesktop"} />
        </div>

        <div className={clsx(styles.section2, "column ai-center")}>
          <Spacer height={150} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
          <div className={styles.headline}>
            {t("about.artGenerator.headline")}
          </div>
          <Spacer height={6} />
          <p
            dangerouslySetInnerHTML={{ __html: t("about.artGenerator.p1") }}
            className={clsx(styles.p, styles.artGeneratorText)}
          />
          <Spacer height={107} className={"hideOnTablet"} />
          <Spacer height={46} className={"hideOnDesktop"} />
          <div className={clsx("relative", styles.browserAndVideoContainer)}>
            <Waypoint onEnter={() => setIsVideoVisible(true)} />
            <Image
              priority
              src={"/about/browser-header.png"}
              alt={"browser"}
              width={910}
              height={52}
            />
            <div className={styles.videoContainer}>
              {isVideoVisible ? (
                <video
                  src={
                    "https://rcsa.s3.eu-west-2.amazonaws.com/rcsa-generator.mp4"
                  }
                  autoPlay
                  muted
                  loop
                />
              ) : (
                <div />
              )}
            </div>
          </div>
          <Spacer height={325} className={"hideOnTablet"} />
          <Spacer height={150} className={"hideOnDesktop"} />
          <div className={styles.letsCreateHistory}>
            {t("about.letsCreateHistory")}
          </div>
          <Spacer height={40} className={"hideOnTablet"} />
          <Spacer height={24} className={"hideOnDesktop"} />
          <div className={"button"} onClick={() => router.push("/")}>
            {t("participateNow")}
          </div>
          <Spacer height={75} className={"hideOnTablet"} />
          <Spacer height={100} className={"hideOnDesktop"} />
        </div>
      </div>
    </>
  );
};

export default About;

export const ArrowLeft: React.FC<{
  onClick(): void;
  size?: number;
  arrowSize?: number;
}> = ({ onClick, size = 28.33, arrowSize = 11.33 }) => (
  <div
    className={clsx(styles.arrow, styles.arrowLeft)}
    onClick={onClick}
    style={{
      width: size,
      height: size,
    }}
  >
    <Image
      priority
      src={"/arrow.svg"}
      alt={"arrow left"}
      width={arrowSize}
      height={arrowSize}
    />
  </div>
);

export const ArrowRight: React.FC<{
  onClick(): void;
  size?: number;
  arrowSize?: number;
}> = ({ onClick, size = 28.33, arrowSize = 11.33 }) => (
  <div
    className={styles.arrow}
    onClick={onClick}
    style={{
      width: size,
      height: size,
    }}
  >
    <Image
      priority
      src={"/arrow.svg"}
      alt={"arrow right"}
      width={arrowSize}
      height={arrowSize}
    />
  </div>
);

interface TweetsProps {
  tablet: boolean;
}

const Tweets: React.FC<TweetsProps> = ({ tablet }) => {
  const [swiper, setSwiper] = useState<SwiperCore>();
  const [_, setCurrentIndex] = useState(0);
  const tweets = useMemo(
    () => [
      "1482470288281726978",
      "1484762982655479809",
      "1482471026810257409",
      "1484448384417214473",
      "1484608880080572416",
      "1461437035709972481",
      "1486119320560230403",
      "1484246971208642577",
      "1483452802085097473",
      "1486321604263043081",
      "1485110680487350275",
      "1486269991028953093",
      "1485411267711885316",
      "1484269206170443779",
      "1484244495218655233",
      "1465173391254573061",
      "1458547854323163140",
      "1449218433921527809",
      "1389316685329489924",
      "1444959355204521993",
      "1481795158869950469",
      "1444405988413554691",
      "1481672396746616832",
      "1461992469109870599",
      "1485321005367054338",
    ],
    []
  );

  return (
    <div className={"row"}>
      {!tablet &&
        (swiper?.isBeginning ? (
          <Spacer width={42} height={42} />
        ) : (
          <div className={"column"}>
            <Spacer height={200} />
            <ArrowLeft
              size={42}
              arrowSize={14}
              onClick={() => swiper?.slidePrev()}
            />
          </div>
        ))}
      <Spacer width={40} className="hideOnTablet" />
      <Swiper
        onSwiper={(swiper) => setSwiper(swiper)}
        onSlideChange={(s) => setCurrentIndex(s.activeIndex)}
        spaceBetween={12}
        className={styles.swiper}
        slidesPerView={1.1}
        draggable={false}
        allowTouchMove={true}
        breakpoints={{
          [tabletMaxWidth]: {
            spaceBetween: 30.09,
            slidesPerView: 3.5,
          },
        }}
        threshold={12}
        lazy
      >
        {tweets.map((tweetId, index) => {
          return (
            <SwiperSlide key={`tweet_${index}`}>
              <div className={tablet ? "pointer-events-none" : ""}>
                <DelayRender delay={index * 1000}>
                  <TwitterTweetEmbed
                    tweetId={tweetId}
                    options={{
                      conversation: "none",
                      dnt: true,
                    }}
                    placeholder={"Loading tweet..."}
                  />
                </DelayRender>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <Spacer width={40} className="hideOnTablet" />
      {!tablet &&
        (swiper?.isEnd ? (
          <Spacer width={42} height={42} />
        ) : (
          <div className={"column"}>
            <Spacer height={200} />
            <ArrowRight
              size={42}
              arrowSize={14}
              onClick={() => swiper?.slideNext()}
            />
          </div>
        ))}
    </div>
  );
};

interface InspiredByCardsProps {
  tablet: boolean;
}

const InspiredByCards: React.FC<InspiredByCardsProps> = ({ tablet }) => {
  const { t } = useTranslation();
  const [swiperInspired, setSwiperInspired] = useState<SwiperCore>();
  const [__, setCurrentInspiredIndex] = useState(0);
  const inspiredByItems = useMemo(
    () => [
      {
        screenshot: inspiredScreenshot1,
        text: t("about.inspiredBy.p1"),
        url: "https://www.tate.org.uk/art/artworks/duchamp-fountain-t07573",
      },
      {
        screenshot: inspiredScreenshot2,
        text: t("about.inspiredBy.p2"),
        url: "https://opensea.io/assets/ethereum/0xff9c1b15b16263c61d017ee9f65c50e4ae0113d7/1",
      },
      {
        screenshot: inspiredScreenshot3,
        text: t("about.inspiredBy.p3"),
        url: "https://www.artsy.net/artwork/piero-manzoni-corpo-daria-body-of-air",
      },
      {
        screenshot: inspiredScreenshot4,
        text: t("about.inspiredBy.p4"),
        url: "https://en.wikipedia.org/wiki/Comedian_(artwork)",
      },
      {
        screenshot: inspiredScreenshot5,
        text: t("about.inspiredBy.p5"),
        url: "https://opensea.io/assets/ethereum/0xda22422592ee3623c8d3c40fe0059cdecf30ca79/2",
      },
      {
        screenshot: inspiredScreenshot6,
        text: t("about.inspiredBy.p6"),
        url: "https://foundation.app/@visualizevalue/foundation/64591",
      },
    ],
    [t]
  );

  const windowSize = useWindowSize();
  const cardRef = useRef<HTMLAnchorElement>(null);
  const slidesPerView = useMemo(() => {
    if (tablet) {
      return Math.max(
        windowSize.width != null
          ? (windowSize.width - 48) / (cardRef.current?.clientWidth ?? 348)
          : 1.2,
        1.2
      );
    }

    return 2.3;
  }, [windowSize.width, tablet]);

  return (
    <div className={clsx("row", styles.inspiredByContainer)}>
      {!tablet &&
        (swiperInspired?.isBeginning ? (
          <Spacer width={42} height={42} />
        ) : (
          <div className={"column"}>
            <Spacer height={307} />
            <ArrowLeft
              size={42}
              arrowSize={14}
              onClick={() => swiperInspired?.slidePrev()}
            />
          </div>
        ))}
      <Spacer width={40} className={"hideOnTablet"} />
      <Swiper
        onSwiper={(swiper) => setSwiperInspired(swiper)}
        onSlideChange={(s) => setCurrentInspiredIndex(s.activeIndex)}
        className={styles.swiperInspired}
        spaceBetween={6}
        slidesPerView={slidesPerView}
        draggable
        allowTouchMove
        breakpoints={{
          [tabletMaxWidth]: {
            spaceBetween: 64,
            slidesPerView: 2.3,
          },
        }}
        threshold={12}
      >
        {inspiredByItems.map((item, index) => {
          return (
            <SwiperSlide key={`inspired_${index}`}>
              <Link href={item.url}>
                <a
                  className={clsx("column ai-center", styles.inspiredByCard)}
                  ref={cardRef}
                  target={"_blank"}
                >
                  <Image
                    placeholder={"blur"}
                    src={item.screenshot}
                    width={400}
                    height={406}
                    alt={`screenshot ${index + 1}`}
                    objectFit={"cover"}
                  />
                  <div className={styles.inspiredByCardInfo}>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: item.text,
                      }}
                      className={clsx(styles.p, "text-align-center")}
                    />
                  </div>
                </a>
              </Link>
            </SwiperSlide>
          );
        })}
      </Swiper>
      <Spacer width={40} className="hideOnTablet" />
      {!tablet &&
        (swiperInspired?.isEnd ? (
          <Spacer width={42} height={42} />
        ) : (
          <div className={"column"}>
            <Spacer height={307} />
            <ArrowRight
              size={42}
              arrowSize={14}
              onClick={() => swiperInspired?.slideNext()}
            />
          </div>
        ))}
    </div>
  );
};
